import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "@icons";
import type { LucideIcon } from "@icons";
import classNames from "classnames";
import { ReactNode, useState } from "react";

export type InlineMessageTone = "success" | "info" | "caution" | "error";

/** One entry in a field-error list — the backend 422 shape. */
export type InlineMessageItem = string | { field?: string; message: string };

export interface InlineMessageAction {
  label: ReactNode;
  onClick?: () => void;
  /** solid (filled), ghost (outlined, default), or link (inline underline). */
  variant?: "solid" | "ghost" | "link";
  icon?: ReactNode;
}

export interface InlineMessageProps {
  /** Colour + icon + default aria role. */
  tone?: InlineMessageTone;
  /** Optional bold heading. */
  title?: ReactNode;
  /** Body content. */
  children?: ReactNode;
  /** Body content shorthand; used when `children` is not provided. */
  message?: ReactNode;
  /** Field-error list (e.g. a 422 response); rendered as bullets with optional field badges. */
  items?: InlineMessageItem[];
  /** Buttons/links rendered below the body. */
  actions?: InlineMessageAction[];
  /** Show a × that hides the message and calls {@link onDismiss}. */
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Dense single-line layout (icon + text). */
  compact?: boolean;
  /** Replace the tone icon. */
  icon?: ReactNode;
  /** Override the aria role (defaults: error/caution → "alert", else "status"). */
  role?: "alert" | "status";
  /** Accessible label for the dismiss button. */
  dismissLabel?: string;
  className?: string;
}

interface ToneStyle {
  Icon: LucideIcon;
  container: string;
  icon: string;
  title: string;
  strong: string;
  bullet: string;
  fieldBadge: string;
  dismiss: string;
  solid: string;
  ghost: string;
}

const TONES: Record<InlineMessageTone, ToneStyle> = {
  success: {
    Icon: CircleCheck,
    container: "bg-green-50 border-green-200",
    icon: "text-green-600",
    title: "text-green-800",
    strong: "text-green-700",
    bullet: "bg-green-600",
    fieldBadge: "bg-green-100 text-green-700",
    dismiss: "text-green-600 hover:bg-green-100",
    solid: "bg-green-600 text-white hover:bg-green-700",
    ghost: "border-green-200 text-green-700 hover:bg-green-100",
  },
  info: {
    Icon: Info,
    container: "bg-blue-50 border-blue-200",
    icon: "text-primary-300",
    title: "text-primary-700",
    strong: "text-primary-600",
    bullet: "bg-primary-300",
    fieldBadge: "bg-blue-100 text-primary-700",
    dismiss: "text-primary-300 hover:bg-blue-100",
    solid: "bg-primary-500 text-white hover:bg-primary-600",
    ghost: "border-blue-200 text-primary-600 hover:bg-blue-100",
  },
  caution: {
    Icon: TriangleAlert,
    container: "bg-amber-50 border-amber-200",
    icon: "text-amber-500",
    title: "text-amber-800",
    strong: "text-amber-700",
    bullet: "bg-amber-500",
    fieldBadge: "bg-amber-100 text-amber-700",
    dismiss: "text-amber-500 hover:bg-amber-100",
    solid: "bg-amber-500 text-white hover:bg-amber-600",
    ghost: "border-amber-200 text-amber-700 hover:bg-amber-100",
  },
  error: {
    Icon: CircleAlert,
    container: "bg-red-50 border-red-200",
    icon: "text-red-500",
    title: "text-red-800",
    strong: "text-red-700",
    bullet: "bg-red-500",
    fieldBadge: "bg-red-100 text-red-700",
    dismiss: "text-red-500 hover:bg-red-100",
    solid: "bg-red-500 text-white hover:bg-red-600",
    ghost: "border-red-200 text-red-700 hover:bg-red-100",
  },
};

/**
 * Contextual, block-level feedback placed inline where it belongs (below a
 * field, above a submit button, atop a section) — the in-context alternative to
 * a detached toast. Four tones, an optional title, body, a field-error list for
 * 422 responses, action buttons, a compact single-line mode, and an optional
 * dismiss ×.
 */
export function InlineMessage({
  tone = "info",
  title,
  children,
  message,
  items,
  actions,
  dismissible = false,
  onDismiss,
  compact = false,
  icon,
  role,
  dismissLabel = "Dismiss",
  className,
}: InlineMessageProps) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const body = children ?? message;
  const style = TONES[tone];
  const Icon = style.Icon;
  const ariaRole =
    role ?? (tone === "error" || tone === "caution" ? "alert" : "status");

  return (
    <div
      role={ariaRole}
      className={classNames(
        "flex gap-[11px] rounded-lg border font-sans leading-normal text-gray-700",
        compact
          ? "items-center px-[11px] py-2 text-[12.5px]"
          : "items-start px-[13px] py-3 text-[13px]",
        style.container,
        className,
      )}
    >
      <span
        className={classNames(
          "flex shrink-0",
          !compact && "mt-[0.5px]",
          style.icon,
        )}
      >
        {icon ?? <Icon size={compact ? 16 : 17} strokeWidth={2} />}
      </span>

      <div
        className={classNames(
          "min-w-0 flex-1",
          compact && "flex items-baseline gap-1.5",
        )}
      >
        {title && (
          <div
            className={classNames(
              "font-semibold leading-snug",
              compact ? "text-[12.5px]" : "text-[13px]",
              style.title,
            )}
          >
            {title}
          </div>
        )}

        {body && (
          <div
            className={classNames(
              "text-gray-700",
              title && !compact && "mt-[3px]",
            )}
          >
            {body}
          </div>
        )}

        {items && items.length > 0 && (
          <ul className="mt-[7px] flex flex-col gap-[5px]">
            {items.map((item, index) => {
              const field = typeof item === "object" ? item.field : undefined;
              const message = typeof item === "object" ? item.message : item;
              return (
                <li key={index} className="relative pl-4 text-gray-700">
                  <span
                    className={classNames(
                      "absolute left-[3px] top-[8px] h-1 w-1 rounded-full",
                      style.bullet,
                    )}
                  />
                  {field && (
                    <span
                      className={classNames(
                        "mr-1 rounded-sm px-[5px] py-px font-mono text-[11.5px] font-semibold",
                        style.fieldBadge,
                      )}
                    >
                      {field}
                    </span>
                  )}
                  {message}
                </li>
              );
            })}
          </ul>
        )}

        {actions && actions.length > 0 && (
          <div className="mt-[11px] flex flex-wrap items-center gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                onClick={action.onClick}
                className={classNames(
                  "inline-flex items-center gap-1.5 text-[12.5px] font-semibold transition-colors",
                  action.variant === "solid"
                    ? classNames(
                        "h-[30px] rounded border border-transparent px-3",
                        style.solid,
                      )
                    : action.variant === "link"
                      ? classNames(
                          "underline decoration-1 underline-offset-2",
                          style.strong,
                        )
                      : classNames("h-[30px] rounded border px-3", style.ghost),
                )}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className={classNames(
            "grid h-6 w-6 shrink-0 place-items-center rounded opacity-70 transition-opacity hover:opacity-100",
            compact ? "-mr-[3px]" : "-mr-[3px] -mt-[3px]",
            style.dismiss,
          )}
        >
          <X size={16} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

export default InlineMessage;
